import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Phone } from "lucide-react";
import { EASE, scrollToSection } from "@/App";

const Line = ({ children, delay = 0, className = "" }) => (
  <span className="block overflow-hidden">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.3, ease: EASE, delay }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const { scrollY } = useScroll();
  const yBg = useTransform(scrollY, [0, 900], [0, 180]);
  const fade = useTransform(scrollY, [0, 600], [1, 0.2]);

  return (
    <section
      id="accueil"
      data-testid="hero-section"
      className="relative h-[100svh] min-h-[640px] overflow-hidden flex items-end"
    >
      <motion.div style={{ y: yBg }} className="absolute inset-0 -bottom-48">
        <img
          src="/images/salle-de-bain-gris.png"
          alt="Salle de bain rénovée haut de gamme par Salle de Bain Expert"
          className="w-full h-full object-cover"
          data-testid="hero-image"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#5b676f] via-[#5b676f]/55 to-[#5b676f]/30" />
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: -30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1.2, ease: EASE, delay: 0.1 }}
        className="absolute top-24 md:top-28 inset-x-0 z-10 flex flex-col items-center px-6"
      >
        <img
          src="/images/logo.png"
          alt="Salle de Bain Expert — Votre satisfaction en rénovation"
          data-testid="hero-logo-image"
          className="w-64 md:w-96 h-auto drop-shadow-[0_8px_24px_rgba(0,0,0,0.6)]"
        />
      </motion.div>

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-10 pb-16 md:pb-24"
      >
        <Line delay={0.2}>
          <span className="uppercase tracking-[0.3em] text-xs text-[#D1BFAE]">
            Entreprise de rénovation — de A à Z
          </span>
        </Line>

        <h1 className="font-display mt-6 leading-[0.95] text-[#F9F9F9]">
          <Line delay={0.35} className="text-4xl sm:text-5xl lg:text-6xl">
            Salle de Bain
          </Line>
          <Line delay={0.5} className="text-4xl sm:text-5xl lg:text-6xl">
            <em className="text-[#D1BFAE]">Expert</em>
          </Line>
        </h1>

        <div className="mt-8" data-testid="hero-tagline">
          <Line delay={0.7}>
            <span className="text-lg sm:text-xl tracking-[0.2em] uppercase text-[#F9F9F9]/90 font-light">
              Votre satisfaction en rénovation
            </span>
          </Line>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: EASE, delay: 1 }}
          className="mt-10 flex flex-wrap items-center gap-5"
        >
          <button
            data-testid="hero-quote-cta"
            onClick={() => scrollToSection("soumission")}
            className="relative overflow-hidden bg-[#D1BFAE] text-[#1a1a1a] px-7 py-3.5 text-xs uppercase tracking-[0.15em] group"
          >
            <span className="absolute inset-0 bg-[#F9F9F9] translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-out" />
            <span className="relative">Demander une soumission</span>
          </button>
          <span
            data-testid="hero-rbq"
            className="text-xs uppercase tracking-[0.15em] text-[#C4C9CE]"
          >
            RBQ : Q:5881-6240-01
          </span>
          <a
            data-testid="hero-phone-link"
            href="tel:4389851797"
            className="flex items-center gap-2 text-sm text-[#F9F9F9] hover:text-[#D1BFAE] transition-colors duration-300"
          >
            <Phone size={16} strokeWidth={1.5} />
            438-985-1797
          </a>
        </motion.div>
      </motion.div>

      <motion.button
        data-testid="hero-scroll-indicator"
        onClick={() => scrollToSection("apropos")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
        className="absolute bottom-8 right-6 md:right-10 z-10 text-[#C4C9CE] hover:text-[#D1BFAE] transition-colors duration-300"
        aria-label="Défiler vers le bas"
      >
        <motion.span
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
          className="block"
        >
          <ArrowDown size={20} strokeWidth={1.25} />
        </motion.span>
      </motion.button>
    </section>
  );
}
